import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useTranslation } from "react-i18next";

import {
  BookingFollowUpStatus,
  BookingFollowUpType,
  Employee,
} from "../../process/types/processTypes";
import { FollowUpStatusChip } from "./FollowUpStatusChip";
import {
  FOLLOW_UP_STATUS_ORDER,
  effectiveFollowUpStatus,
  getFollowUpForType,
  nextFollowUpStatus,
} from "../utils/followUpHelpers";

interface Props {
  employee: Employee;
  type: BookingFollowUpType;
  targetStatus?: BookingFollowUpStatus;
  onSubmit: (payload: {
    status: BookingFollowUpStatus;
    scheduledDate?: Date;
    remark?: string;
  }) => Promise<void>;
  onClose: () => void;
  submitting?: boolean;
  locale?: string;
}

export function FollowUpAdvanceDialog({
  employee,
  type,
  targetStatus,
  onSubmit,
  onClose,
  submitting,
  locale = "en",
}: Props) {
  const { t } = useTranslation();
  const { t: te } = useTranslation("enums");

  const followUp = getFollowUpForType(employee, type);
  const currentStatus = effectiveFollowUpStatus(followUp);
  const status =
    targetStatus ?? nextFollowUpStatus(currentStatus) ?? currentStatus;

  const [scheduledDate, setScheduledDate] = useState<Date | null>(
    followUp?.scheduledDate ? new Date(followUp.scheduledDate) : null,
  );
  const [remark, setRemark] = useState(followUp?.remark ?? "");

  const syncPreview = useMemo(() => {
    if (status !== BookingFollowUpStatus.employee_completed) return null;
    switch (type) {
      case BookingFollowUpType.medical:
        return te("completed");
      case BookingFollowUpType.tasheer:
        return t("followUps.syncBiometrics");
      case BookingFollowUpType.flight:
        return t("followUps.syncFlight");
      default:
        return null;
    }
  }, [status, type, t, te]);

  const handleSave = async () => {
    if (
      status === BookingFollowUpStatus.booked_with_date &&
      !scheduledDate
    ) {
      return;
    }
    await onSubmit({
      status,
      scheduledDate: scheduledDate ?? undefined,
      remark: remark.trim() || undefined,
    });
  };

  const activeStep = FOLLOW_UP_STATUS_ORDER.indexOf(status);

  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      <Stepper activeStep={activeStep >= 0 ? activeStep : 0} alternativeLabel>
        {FOLLOW_UP_STATUS_ORDER.map((step) => (
          <Step key={step} completed={FOLLOW_UP_STATUS_ORDER.indexOf(step) < activeStep}>
            <StepLabel>{te(`bookingFollowUp_${step}`)}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box>
        <FollowUpStatusChip status={currentStatus} />
      </Box>

      {status === BookingFollowUpStatus.booked_with_date && (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
          <DatePicker
            label={t("followUps.scheduledDate")}
            views={["year", "month", "day"]}
            disableMaskedInput
            inputFormat="DD-MMM-YYYY"
            value={scheduledDate}
            onChange={(date) => setScheduledDate(date as Date | null)}
            renderInput={(params) => (
              <TextField {...params} fullWidth size="small" required />
            )}
          />
        </LocalizationProvider>
      )}

      {status === BookingFollowUpStatus.confirmed_by_employee &&
        followUp?.scheduledDate && (
          <Alert severity="info">
            {t("followUps.confirmPrompt", {
              date: new Date(followUp.scheduledDate).toLocaleDateString(),
            })}
          </Alert>
        )}

      {syncPreview && (
        <Alert severity="success">{t("followUps.willSync", { field: syncPreview })}</Alert>
      )}

      <TextField
        label={t("remark")}
        value={remark}
        onChange={(e) => setRemark(e.target.value)}
        multiline
        minRows={2}
        fullWidth
        size="small"
      />

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button onClick={onClose} disabled={submitting}>
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={
            submitting ||
            (status === BookingFollowUpStatus.booked_with_date && !scheduledDate)
          }
        >
          {te(`bookingFollowUp_${status}`)}
        </Button>
      </Stack>
    </Stack>
  );
}
